import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, TrendingUp, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { LimitCheckResult } from "./subscription.types";

interface UpgradeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	limitType: "chat" | "storage" | "feature";
	limitCheck?: LimitCheckResult;
	featureName?: string;
	upgradeUrl?: string;
}

/**
 * Reusable upgrade dialog component for subscription limits
 * Shows a beautiful upgrade prompt when users hit their plan limits
 *
 * @example
 * ```tsx
 * const { chatLimit } = useSubscription();
 * const [showUpgrade, setShowUpgrade] = useState(false);
 *
 * if (!chatLimit.allowed) {
 *   setShowUpgrade(true);
 * }
 *
 * <UpgradeDialog
 *   open={showUpgrade}
 *   onOpenChange={setShowUpgrade}
 *   limitType="chat"
 *   limitCheck={chatLimit}
 * />
 * ```
 */
export function UpgradeDialog({
	open,
	onOpenChange,
	limitType,
	limitCheck,
	featureName,
	upgradeUrl = "/pricing",
}: UpgradeDialogProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleUpgrade = () => {
		onOpenChange(false);
		navigate(upgradeUrl);
	};

	const getContent = () => {
		switch (limitType) {
			case "chat":
				return {
					icon: <Sparkles className="h-12 w-12 text-orange-500" />,
					title: t("subscription.upgradeDialog.chat.title"),
					description: limitCheck
						? t("subscription.upgradeDialog.chat.description", {
								current: limitCheck.current,
								max: limitCheck.maxAllowed,
							})
						: t("subscription.upgradeDialog.chat.descriptionSimple"),
				};
			case "storage":
				return {
					icon: <TrendingUp className="h-12 w-12 text-orange-500" />,
					title: t("subscription.upgradeDialog.storage.title"),
					description: limitCheck
						? t("subscription.upgradeDialog.storage.description", {
								current: limitCheck.current,
								max: limitCheck.maxAllowed,
							})
						: t("subscription.upgradeDialog.storage.descriptionSimple"),
				};
			case "feature":
				return {
					icon: <Zap className="h-12 w-12 text-orange-500" />,
					title: t("subscription.upgradeDialog.feature.title"),
					description: t("subscription.upgradeDialog.feature.description", {
						feature:
							featureName ||
							t("subscription.upgradeDialog.feature.defaultFeature"),
					}),
				};
			default:
				return {
					icon: <Sparkles className="h-12 w-12 text-orange-500" />,
					title: t("subscription.upgradeDialog.generic.title"),
					description: t("subscription.upgradeDialog.generic.description"),
				};
		}
	};

	const content = getContent();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<div className="flex justify-center mb-4">
						<div className="relative">
							{content.icon}
							<div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-orange-300 rounded-full blur opacity-30 animate-pulse" />
						</div>
					</div>
					<DialogTitle className="text-center text-2xl">
						{content.title}
					</DialogTitle>
					<DialogDescription className="text-center text-base pt-2">
						{content.description}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 py-4">
					<div className="flex items-start gap-3">
						<div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500/10">
							<Sparkles className="h-3.5 w-3.5 text-orange-500" />
						</div>
						<div>
							<p className="text-sm font-medium">
								{t("subscription.upgradeDialog.benefits.unlimited")}
							</p>
							<p className="text-xs text-muted-foreground">
								{t("subscription.upgradeDialog.benefits.unlimitedDesc")}
							</p>
						</div>
					</div>
					<div className="flex items-start gap-3">
						<div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500/10">
							<TrendingUp className="h-3.5 w-3.5 text-orange-500" />
						</div>
						<div>
							<p className="text-sm font-medium">
								{t("subscription.upgradeDialog.benefits.advanced")}
							</p>
							<p className="text-xs text-muted-foreground">
								{t("subscription.upgradeDialog.benefits.advancedDesc")}
							</p>
						</div>
					</div>
					<div className="flex items-start gap-3">
						<div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500/10">
							<Zap className="h-3.5 w-3.5 text-orange-500" />
						</div>
						<div>
							<p className="text-sm font-medium">
								{t("subscription.upgradeDialog.benefits.priority")}
							</p>
							<p className="text-xs text-muted-foreground">
								{t("subscription.upgradeDialog.benefits.priorityDesc")}
							</p>
						</div>
					</div>
				</div>

				<DialogFooter className="flex flex-col sm:flex-row gap-2">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="w-full sm:w-auto"
					>
						{t("subscription.upgradeDialog.actions.later")}
					</Button>
					<Button
						onClick={handleUpgrade}
						className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 text-white"
					>
						{t("subscription.upgradeDialog.actions.upgrade")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
